import React, { useEffect, useState } from "react";
import { Outlet, NavLink, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TID } from "@/lib/testIds";
import api from "@/lib/api";
import { LayoutDashboard, Package, Layers, Home, Settings, Music, BookOpen, FileText, Users, LogOut, ExternalLink, ShoppingBag, Tag, Gift, Mail, Rocket, HelpCircle, Sparkles, Timer, Camera, RotateCcw, Send, Download } from "lucide-react";

const NAV = [
  ["", "Overview", LayoutDashboard],
  ["orders", "Orders", ShoppingBag],
  ["returns", "Returns", RotateCcw],
  ["products", "Products", Package],
  ["crafts", "Crafts (Digital)", Download],
  ["collections", "Collections & Drops", Layers],
  ["discounts", "Discounts", Tag],
  ["gift-cards", "Gift Cards", Gift],
  ["mail", "Mail", Mail],
  ["newsletter", "Newsletter", Send],
  ["homepage", "Homepage", Home],
  ["journal", "Journal", BookOpen],
  ["lookbook", "Lookbook", Camera],
  ["pages", "Pages", FileText],
  ["faq", "FAQ", HelpCircle],
  ["drop", "Launch Drop", Sparkles],
  ["launch", "Launch Mode", Timer],
  ["sound", "Sound", Music],
  ["settings", "Store Settings", Settings],
  ["integrations", "Go Live", Rocket],
  ["lists", "Customers & Lists", Users],
];

const ADMIN_ROLES = ["owner", "store_manager", "content", "client_care"];

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => api.get("/admin/emails/unread-count").then(({ data }) => { if (alive) setUnread(data.count || 0); }).catch(() => {});
    tick();
    const t = setInterval(tick, 60000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  if (user === null) return <div className="min-h-screen bg-cocoa" />;
  if (user === false) return <Navigate to="/admin/login" replace />;
  if (!ADMIN_ROLES.includes(user.role)) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-sand flex">
      <aside className="w-60 bg-cocoa text-cream flex flex-col fixed h-full">
        <div className="px-6 py-6 border-b border-white/10">
          <p className="font-serif text-xl tracking-[0.12em]">NOIRBYAMARA</p>
          <p className="text-[0.62rem] tracking-[0.16em] uppercase text-gold mt-1">Atelier CMS</p>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto">
          {NAV.map(([path, label, Icon]) => (
            <NavLink key={path} to={`/admin/${path}`} end={path === ""} data-testid={TID.adminNav(path || "overview")}
              onClick={(e) => { if (window.__nbaEditorDirty && !window.confirm("You have unsaved changes. Leave without saving?")) e.preventDefault(); }}
              className={({ isActive }) => `flex items-center gap-3 px-6 py-3 text-[0.74rem] tracking-[0.08em] transition-colors ${isActive ? "bg-cream/10 text-cream border-l-2 border-gold" : "text-cream/60 hover:text-cream"}`}>
              <Icon size={16} strokeWidth={1.5} /> <span>{label}</span>
              {path === "mail" && unread > 0 && (
                <span data-testid="mail-unread-badge" className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-wine text-cream text-[0.6rem] flex items-center justify-center">{unread}</span>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-4 space-y-2">
          <a href="/" target="_blank" rel="noreferrer" className="flex items-center gap-2 text-[0.7rem] tracking-[0.1em] uppercase text-cream/60 hover:text-cream px-2 py-2"><ExternalLink size={14} /> View Store</a>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="flex items-center gap-2 text-[0.7rem] tracking-[0.1em] uppercase text-cream/60 hover:text-cream px-2 py-2"><LogOut size={14} /> Sign Out</button>
        </div>
      </aside>
      <main className="flex-1 ml-60 p-8 md:p-10 text-espresso">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
